import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ValidateResult, ValidatorSessionSummary } from '../api/client'
import {
  deleteValidatorSession,
  downloadValidatorPdf,
  downloadValidatorXml,
  getValidatorHistory,
  openValidatorSession,
} from '../api/client'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
  onOpen?: (result: ValidateResult, filename: string) => void
}

export function ValidatorHistoryPanel({ onOpen }: Props) {
  const { t } = useTranslation()
  const [sessions, setSessions]   = useState<ValidatorSessionSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError]         = useState<string | null>(null)
  const [deleting, setDeleting]   = useState<number | null>(null)
  const [opening, setOpening]     = useState<number | null>(null)

  const load = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getValidatorHistory()
      setSessions(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('validator_history.error_loading'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (id: number) => {
    if (!confirm(t('validator_history.confirm_delete'))) return
    setDeleting(id)
    try {
      await deleteValidatorSession(id)
      setSessions(prev => prev.filter(s => s.id !== id))
    } catch (e) {
      alert(e instanceof Error ? e.message : t('validator_history.error_deleting'))
    } finally {
      setDeleting(null)
    }
  }

  const handleOpen = async (s: ValidatorSessionSummary) => {
    if (!onOpen) return
    setOpening(s.id)
    try {
      const result = await openValidatorSession(s.id)
      onOpen(result, s.filename)
    } catch (e) {
      alert(e instanceof Error ? e.message : t('validator_history.error_opening'))
    } finally {
      setOpening(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
        <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        {t('common.loading')}
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-300">
        {error}
        <button onClick={load} className="ml-3 underline text-red-600 hover:text-red-800">{t('validator_history.retry')}</button>
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <div className="text-4xl mb-3 opacity-30">📋</div>
        <p className="text-sm font-medium">{t('validator_history.empty_title')}</p>
        <p className="text-xs mt-1">{t('validator_history.empty_description')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-400 dark:text-gray-500">{t('validator_history.sessions_count', { count: sessions.length })}</span>
        <button
          onClick={load}
          className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title={t('validator_history.refresh')}
        >
          ↻ {t('validator_history.refresh')}
        </button>
      </div>

      {sessions.map(s => (
        <div
          key={s.id}
          className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 px-4 py-3 flex items-start gap-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
        >
          {/* Status-icoon */}
          <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${
            s.is_valid && s.error_count === 0
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {s.is_valid && s.error_count === 0 ? '✓' : '✗'}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-gray-400 dark:text-gray-500 shrink-0">#{s.id}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[260px]" title={s.filename}>
                {s.filename}
              </span>
              {s.file_size !== null && (
                <span className="text-xs text-gray-400">{formatSize(s.file_size)}</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
              <span className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded font-medium">{s.standard}</span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span>{s.document_type}</span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span>{formatDate(s.created_at)}</span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span>{s.uploaded_by}</span>
            </div>
            {(s.error_count > 0 || s.warning_count > 0) && (
              <div className="flex items-center gap-2 text-xs">
                {s.error_count > 0 && (
                  <span className="text-red-600 font-medium">
                    ✗ {t('validator_history.error_count', { count: s.error_count })}
                  </span>
                )}
                {s.warning_count > 0 && (
                  <span className="text-amber-600">
                    ⚠ {t('validator_history.warning_count', { count: s.warning_count })}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Acties */}
          <div className="shrink-0 flex items-center gap-1.5">
            {onOpen && (
              <button
                onClick={() => handleOpen(s)}
                disabled={opening === s.id}
                className="text-xs px-2 py-1 border border-blue-200 dark:border-blue-800/50 rounded-lg text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 transition-colors font-medium disabled:opacity-50"
                title={t('validator_history.open_title')}
              >
                {opening === s.id ? '…' : t('validator_history.open')}
              </button>
            )}
            <button
              onClick={() => downloadValidatorXml(s.id, s.filename)}
              className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 transition-colors"
              title={t('validator_history.download_xml_title')}
            >
              ↓ XML
            </button>
            {s.has_pdf && (
              <button
                onClick={() => downloadValidatorPdf(s.id, s.filename)}
                className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 transition-colors"
                title={t('validator_history.download_pdf_title')}
              >
                ↓ PDF
              </button>
            )}
            <button
              onClick={() => handleDelete(s.id)}
              disabled={deleting === s.id}
              className="text-xs px-2 py-1 border border-red-100 dark:border-red-900/40 rounded-lg text-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 hover:border-red-200 transition-colors disabled:opacity-50"
              title={t('validator_history.delete_title')}
            >
              {deleting === s.id ? '…' : '✕'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
